import {ellipsis} from 'polished';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {center} from '../../../../../libs/shared/ui/src/styles/mixins';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {InboxModel} from '../../../../models/api/inboxModel';
import {TagModel} from '../../../../models/api/tagModel';
import {ColorDot} from '../../colorDot';
import {getCellBackgroundColor, getCellIndexStyles, getCellTextColor} from '../helpers/tableCellHelpers';
import {getWidthSettingsStyles} from '../tableColumn';
import {TableCell, TableCellProps, TableCellStyleProps} from './tableCell';

/*
 * Props.
 */

interface InboxesCellProps extends TableCellProps<any> {
  inboxes?: ReadonlyArray<InboxModel>;
}

/*
 * Style.
 */

const StyledDiv = styled.div<TableCellStyleProps>`
  padding: 0 12px;
  overflow: hidden;
  white-space: nowrap;
  align-items: center;
  height: 100%;

  display: grid;
  grid-template-areas: 'icon . label count';
  grid-template-columns: 16px 8px 1fr auto;

  ${(p) => getCellBackgroundColor(p.$isHovering, p.$isSelected)};
  ${(p) => getWidthSettingsStyles(p.$settings?.cellWidth, p.$index)};
  ${(p) =>
    getCellIndexStyles(
      p.$index,
      p.$isSelected,
      p.$shouldRemoveTopBorderRadius,
      p.$shouldRemoveBottomBorderRadius,
    )};
  ${(p) => getCellTextColor(p.$isSelected)};
`;

const StyledCountDiv = styled.div`
  grid-area: count;
  font-weight: ${fontWeights.bold};
`;

const StyledIconDiv = styled.div`
  grid-area: icon;
  ${center()};
`;

const StyledLabelDiv = styled.div`
  ${ellipsis()};
  grid-area: label;
`;

/*
 * Component.
 */

export const InboxesCell: FC<InboxesCellProps> = (props) => {
  const theme = useUserTheme();
  const {
    isSelected,
    isHovering,
    isLoading,
    size,
    settings,
    index,
    shouldShowStickyGradient,
    shouldRemoveTopBorderRadius,
    shouldRemoveBottomBorderRadius,
  } = props;

  const renderContent = () => {
    if (!props.inboxes) {
      return (
        <StyledDiv
          $settings={settings}
          $isHovering={Boolean(isHovering)}
          $index={index}
          $isSelected={Boolean(isSelected)}
          $shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
          $shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
        >
          <StyledIconDiv>
            <Icon name="inboxSmall" />
          </StyledIconDiv>
          <StyledLabelDiv>
            <Trans id="settingstable-anyinbox">Any inbox</Trans>
          </StyledLabelDiv>
        </StyledDiv>
      );
    }

    const firstInbox = props.inboxes[0];
    if (props.inboxes.length === 0 || !firstInbox) {
      return (
        <StyledDiv
          $settings={settings}
          $isHovering={Boolean(isHovering)}
          $index={index}
          $isSelected={Boolean(isSelected)}
          $shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
          $shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
        >
          <StyledIconDiv>
            <Icon name="inboxSmall" />
          </StyledIconDiv>
          <StyledLabelDiv>
            <Trans id="settingstable-noinbox">No inbox</Trans>
          </StyledLabelDiv>
        </StyledDiv>
      );
    }

    const inboxColor = firstInbox.color || theme.alphas.gray20;
    const diff = props.inboxes.length - 1;

    return (
      <StyledDiv
        $settings={settings}
        $isHovering={Boolean(isHovering)}
        $index={index}
        $isSelected={Boolean(isSelected)}
        $shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
        $shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
      >
        <StyledIconDiv>
          <ColorDot size={8} color={inboxColor} hoverColor={inboxColor} />
        </StyledIconDiv>
        <StyledLabelDiv>{firstInbox.name}</StyledLabelDiv>
        {diff ? <StyledCountDiv>, +{diff}</StyledCountDiv> : undefined}
      </StyledDiv>
    );
  };

  return (
    <TableCell<TagModel>
      isSelected={isSelected}
      isHovering={isHovering}
      isLoading={isLoading}
      size={size}
      settings={settings}
      index={index}
      value={props.value}
      shouldShowStickyGradient={shouldShowStickyGradient}
      shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
      shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
      renderCell={renderContent}
    />
  );
};
